import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Warning, Clock, Bell } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const KnowYourGPage = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [dose, setDose] = useState('');
  const [lastDoseTime, setLastDoseTime] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);

  useEffect(() => {
    let interval;
    if (timerActive && lastDoseTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - lastDoseTime) / 1000 / 60);
        setTimeElapsed(elapsed);
        
        // Warning at specific times
        if (elapsed === 90) {
          toast.warning('⏰ 90 minutes since last dose', {
            description: 'Wait at least 2 hours between doses'
          });
        } else if (elapsed === 120) {
          toast.success('✅ Safe to redose window (if needed)', {
            description: 'Remember: start low, go slow'
          });
        }
      }, 10000); // Update every 10 seconds
    }
    return () => clearInterval(interval);
  }, [timerActive, lastDoseTime]);

  const gTypes = [
    {
      id: 'bdo',
      name: 'BDO (Butanediol)',
      emoji: '🧪',
      color: 'from-purple-500 to-pink-500',
      appearance: 'Oily or thicker texture',
      onset: 'Slower to hit, can sneak up on you',
      duration: '4-6 hours',
      startingDose: '2.5ml',
      experiencedDose: '2.5-3.0ml',
      warnings: [
        'Feeling "nothing yet" doesn\'t mean more is safe',
        'Can hit suddenly after feeling mild',
        'Longer lasting than GBL',
        'Be patient - don\'t redose too soon'
      ],
      safetyTips: [
        'Wait at least 2-3 hours between doses',
        'Start with 2.5ml even if experienced',
        'Have a trusted friend nearby',
        'Set a timer for redosing'
      ]
    },
    {
      id: 'gbl',
      name: 'GBL (Gamma-Butyrolactone)',
      emoji: '💧',
      color: 'from-blue-500 to-cyan-500',
      appearance: 'Watery, can sting mouth, damages plastic',
      onset: 'Hits faster than BDO',
      duration: '2-4 hours',
      startingDose: '1.5ml',
      experiencedDose: '2.0ml',
      warnings: [
        'Stronger and easier to overdose',
        'Very small margin between safe and unsafe',
        'Can damage mouth/throat tissue',
        'Don\'t mix with alcohol or benzos'
      ],
      safetyTips: [
        'Measure carefully - every 0.5ml matters',
        'Wait minimum 2 hours between doses',
        'Use glass or metal measuring tools',
        'Don\'t redose if you feel anything'
      ]
    },
    {
      id: 'ghb',
      name: 'GHB (Gamma-Hydroxybutyrate)',
      emoji: '⚗️',
      color: 'from-orange-500 to-red-500',
      appearance: 'Watery, often tastes salty',
      onset: 'Varies - depends on concentration',
      duration: '2-4 hours',
      startingDose: 'Consult source - strength unknown',
      experiencedDose: 'Test carefully - varies wildly',
      warnings: [
        'Strength varies A LOT between batches',
        'You often don\'t know the concentration',
        'Makes it very unpredictable',
        'Highest overdose risk of all G types'
      ],
      safetyTips: [
        'Always test with tiny amount first',
        'Never assume same dose as last time',
        'Wait longer between doses (2-3 hours minimum)',
        'Have naloxone nearby (won\'t reverse G but good for emergencies)'
      ]
    }
  ];

  const handleDoseTaken = (type) => {
    const now = Date.now();
    setLastDoseTime(now);
    setTimerActive(true);
    setTimeElapsed(0);
    toast.success(`⏰ ${type} dose timer started`, {
      description: 'Wait at least 2 hours before redosing'
    });
  };

  const stopTimer = () => {
    setTimerActive(false);
    setLastDoseTime(null);
    setTimeElapsed(0);
    toast.info('Dose timer stopped');
  };

  const selectedGType = gTypes.find(g => g.id === selectedType);

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedType ? setSelectedType(null) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">💊</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedType ? selectedGType?.name : 'Know Your G'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedType ? (
          <>
            <div className="mb-8 p-6 bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 rounded-3xl text-white text-center">
              <div className="text-6xl mb-3">⚗️</div>
              <h2 className="text-2xl font-bold mb-3">Not All G is the Same</h2>
              <p className="text-white/90 leading-relaxed mb-4">
                BDO, GBL, and GHB are different substances with different risks. 
                Know what you're using. Measure carefully. Stay safe. 💜
              </p>
              <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                <p className="text-sm text-white/80">
                  ⚠️ The line between "feels nice" and "unconscious" is VERY thin with G
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {gTypes.map((gType) => (
                <button
                  key={gType.id}
                  onClick={() => setSelectedType(gType.id)}
                  data-testid={`g-type-${gType.id}`}
                  className="w-full group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${gType.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  <div className="relative z-10 flex items-center">
                    <div className="text-5xl mr-4 emoji-bounce">{gType.emoji}</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                        {gType.name}
                      </h3>
                      <p className="text-sm text-[rgb(var(--text-secondary))]">
                        {gType.appearance}
                      </p>
                      <p className="text-xs text-[rgb(var(--primary))] mt-1">
                        Starting dose: {gType.startingDose}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-8 p-6 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/30 rounded-3xl">
              <h3 className="text-lg font-bold text-[rgb(var(--danger))] mb-3 flex items-center">
                <Warning size={24} className="mr-2" />
                Critical G Safety Rules
              </h3>
              <ul className="space-y-2 text-sm text-[rgb(var(--text-secondary))]">
                <li>• Never mix G with alcohol, benzos, or other downers</li>
                <li>• Measure every single dose carefully</li>
                <li>• Wait at least 2 hours between doses</li>
                <li>• Don't leave someone alone if they're fading out</li>
                <li>• If someone is hard to wake, call 911 immediately</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div className={`p-6 bg-gradient-to-br ${selectedGType.color} rounded-3xl text-white`}>
              <div className="text-center">
                <div className="text-6xl mb-3">{selectedGType.emoji}</div>
                <h2 className="text-3xl font-bold mb-2">{selectedGType.name}</h2>
                <p className="text-white/90">{selectedGType.appearance}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
                <p className="text-xs text-[rgb(var(--text-secondary))] mb-1">Onset Time</p>
                <p className="text-sm font-semibold text-[rgb(var(--text-primary))]">{selectedGType.onset}</p>
              </div>
              <div className="p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
                <p className="text-xs text-[rgb(var(--text-secondary))] mb-1">Duration</p>
                <p className="text-sm font-semibold text-[rgb(var(--text-primary))]">{selectedGType.duration}</p>
              </div>
            </div>

            <div className="p-6 bg-[rgb(var(--primary))]/10 border border-[rgb(var(--primary))]/20 rounded-2xl">
              <h3 className="font-bold text-[rgb(var(--text-primary))] mb-3">📏 Dosing Guide</h3>
              <div className="space-y-2 text-sm">
                <p className="text-[rgb(var(--text-secondary))]">
                  <strong className="text-[rgb(var(--text-primary))]">Starting dose:</strong> {selectedGType.startingDose}
                </p>
                <p className="text-[rgb(var(--text-secondary))]">
                  <strong className="text-[rgb(var(--text-primary))]">Experienced dose:</strong> {selectedGType.experiencedDose}
                </p>
                <p className="text-xs text-[rgb(var(--primary))] mt-3">
                  ⚡ Always measure carefully. Every 0.5ml matters!
                </p>
              </div>
            </div>

            {/* Dose Timer */}
            {timerActive ? (
              <div className="p-6 bg-gradient-to-r from-[rgb(var(--success))]/20 to-[rgb(var(--primary))]/20 rounded-2xl border border-[rgb(var(--success))]/30">
                <div className="text-center mb-4">
                  <Clock size={48} className="text-[rgb(var(--primary))] mx-auto mb-2" />
                  <div className="text-4xl font-bold text-[rgb(var(--text-primary))]">
                    {timeElapsed}
                  </div>
                  <div className="text-sm text-[rgb(var(--text-secondary))]">
                    minutes since last dose
                  </div>
                </div>
                {timeElapsed < 120 && (
                  <div className="p-3 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-xl text-center">
                    <p className="text-sm text-[rgb(var(--danger))] font-semibold">
                      ⚠️ Wait {120 - timeElapsed} more minutes before redosing
                    </p>
                  </div>
                )}
                {timeElapsed >= 120 && (
                  <div className="p-3 bg-[rgb(var(--success))]/10 border border-[rgb(var(--success))]/20 rounded-xl text-center">
                    <p className="text-sm text-[rgb(var(--success))] font-semibold">
                      ✅ Safe window (if redosing is necessary)
                    </p>
                  </div>
                )}
                <Button
                  onClick={stopTimer}
                  variant="outline"
                  className="w-full mt-4 border-[rgb(var(--text-secondary))]/30"
                >
                  Stop Timer
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => handleDoseTaken(selectedGType.name)}
                data-testid="start-dose-timer"
                className="w-full min-h-[64px] rounded-full bg-gradient-to-r from-[rgb(var(--primary))] to-[rgb(var(--secondary))] text-white font-bold text-lg btn-hover"
              >
                <Bell size={24} weight="fill" className="mr-2" />
                I Just Took a Dose - Start Timer
              </Button>
            )}

            <div className="space-y-4">
              <h3 className="font-bold text-[rgb(var(--text-primary))]">⚠️ Warnings</h3>
              {selectedGType.warnings.map((warning, idx) => (
                <div key={idx} className="flex items-start p-4 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-xl">
                  <Warning size={20} className="text-[rgb(var(--danger))] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-[rgb(var(--text-secondary))]">{warning}</span>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-[rgb(var(--text-primary))]">💚 Safety Tips</h3>
              {selectedGType.safetyTips.map((tip, idx) => (
                <div key={idx} className="flex items-start p-4 bg-[rgb(var(--success))]/10 border border-[rgb(var(--success))]/20 rounded-xl">
                  <span className="text-[rgb(var(--success))] mr-2 text-lg">✓</span>
                  <span className="text-sm text-[rgb(var(--text-secondary))]">{tip}</span>
                </div>
              ))}
            </div>

            <div className="p-6 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/30 rounded-2xl">
              <h3 className="font-bold text-[rgb(var(--danger))] mb-3">🚨 Overdose Signs</h3>
              <ul className="space-y-1 text-sm text-[rgb(var(--text-secondary))]">
                <li>• Very hard to wake up or unresponsive</li>
                <li>• Slow or irregular breathing</li>
                <li>• Blue lips or fingertips</li>
                <li>• Cold, clammy skin</li>
                <li>• Vomiting while unconscious</li>
              </ul>
              <p className="text-sm font-bold text-[rgb(var(--danger))] mt-3">
                If you see these signs: Call 911 immediately. Put them in recovery position. Stay with them.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default KnowYourGPage;
